import {
  BcItem,
  formatItemVariant,
  Item,
  ItemVariant,
  ShopifyProduct,
  ShopifyProductVariant,
} from "@megaron/catalog-contracts";
import { DiffSyncPage, DiffSyncRecord } from "@megaron/diff-sync-webapp";
import { useClientManager } from "@megaron/react-clients";
import React from "react";

type CatalogItemSyncType = "bcItem" | "sheetsItem" | "shopifyProduct" | "shopifyVariant";
type DiffValue = Record<string, unknown>;

type Props<T extends DiffValue> = {
  title: string;
  type: CatalogItemSyncType;
  directionLabels: {
    pull: string;
    push: string;
  };
  labels: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, (value: unknown) => React.ReactNode>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
};

export const BcItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<BcItem & DiffValue>
    title="Synchronizacja Business Central"
    type="bcItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do BC",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={{
      bcId: "Id BC",
      itemId: "Id",
      itemName: "Nazwa",
      gtin: "EAN",
    }}
  />
);

export const SheetsItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<Item> & DiffValue>
    title="Synchronizacja Google Sheets"
    type="sheetsItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={itemLabels}
    formatters={itemFormatters}
  />
);

export const ShopifyProductSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProduct> & DiffValue>
    title="Synchronizacja Shopify - produkty"
    type="shopifyProduct"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["handle", "title"]}
    labels={shopifyProductLabels}
  />
);

export const ShopifyVariantSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProductVariant> & DiffValue>
    title="Synchronizacja Shopify - warianty"
    type="shopifyVariant"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["sku", "variant"]}
    labels={shopifyVariantLabels}
    formatters={shopifyVariantFormatters}
  />
);

const CatalogItemSyncDiffPage = <T extends DiffValue>(props: Props<T>) => {
  const client = useClientManager("catalog");

  const pullQuery = client.diffQuery().useQuery({
    type: props.type,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    type: props.type,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    type: props.type,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ type: props.type });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    await pullMutation.mutateAsync({ type: props.type, diffIds });
    await refetchDiffs();
  };

  const push = async (diffIds: string[]) => {
    await pushMutation.mutateAsync({ type: props.type, diffIds });
    await refetchDiffs();
  };

  return (
    <DiffSyncPage<T>
      title={props.title}
      pullableDiffs={pullQuery.data as DiffSyncRecord<T>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<T>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<T>[] | undefined}
      directionLabels={props.directionLabels}
      labels={props.labels}
      formatters={props.formatters}
      alwaysVisibleColumns={props.alwaysVisibleColumns}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<DiffValue>) => !!diff.error;

const itemLabels = {
  bcId: "Id BC",
  shopifyProductId: "Id produktu Shopify",
  box: "Karton",
  brand: "Marka",
  cost: "Koszt STD",
  defaultProfit: "Domyślna marża",
  description: "Opis",
  gtin: "EAN",
  handle: "Handle",
  isArchived: "Zarchiwizowany?",
  isInAvailability: "Śledzenie dostępności",
  isLoyalty: "Produkt Specjalista",
  itemId: "Id",
  itemName: "Nazwa",
  layer: "Warstwa",
  massKg: "Masa kg",
  pallet: "Paleta",
  productGroup: "Grupa produktowa",
  productId: "Indeks produktu",
  size: "Wielkość",
  sizeUnit: "Jednostka sprzedaży",
  title: "Tytuł",
  variant: "Wariant",
  shopifyVariantId: "variantId",
} satisfies Partial<Record<keyof Item, string>>;

const itemFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof Item, (value: unknown) => React.ReactNode>>;

const shopifyProductLabels = {
  id: "Id Shopify",
  title: "Tytuł",
  description: "Opis",
  vendor: "Marka",
  handle: "Handle",
  tags: "Tagi",
} satisfies Partial<Record<keyof ShopifyProduct, string>>;

const shopifyVariantLabels = {
  variantId: "Id wariantu",
  sku: "SKU",
  productId: "Id produktu",
  variant: "Wariant",
  barcode: "EAN",
  weightKg: "Masa kg",
} satisfies Partial<Record<keyof ShopifyProductVariant, string>>;

const shopifyVariantFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof ShopifyProductVariant, (value: unknown) => React.ReactNode>>;
